import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TReplyTemplate } from '../types/case.type';

export type TGetReplyTemplatesResponse = ApiResponse<IPagination<TReplyTemplate>>;

export const getReplyTemplates = async (params: FetchParams): Promise<TGetReplyTemplatesResponse> => {
  const url = '/template';

  return axiosInstance.get(url, { params });
};

/**
 * Use Get Reply Templates React-Query Hook
 */
export const GET_REPLY_TEMPLATES_QUERY_KEY = 'get-reply-templates';

export const getReplyTemplatesQueryOptions = <TData = TGetReplyTemplatesResponse>(
  params: FetchParams,
  select?: (data: TGetReplyTemplatesResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_REPLY_TEMPLATES_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getReplyTemplates(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useReplyTemplatesQuery<TData>(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getReplyTemplatesQueryOptions<TData>>;
  select: (data: TGetReplyTemplatesResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useReplyTemplatesQuery(options: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getReplyTemplatesQueryOptions<TGetReplyTemplatesResponse>>;
}): ReturnType<typeof useQuery<TGetReplyTemplatesResponse>>;

// Implementation
export function useReplyTemplatesQuery<TData = TGetReplyTemplatesResponse>({
  params,
  queryConfig,
  select,
}: {
  params: FetchParams;
  queryConfig?: QueryConfig<typeof getReplyTemplatesQueryOptions<TData | TGetReplyTemplatesResponse>>;
  select?: (data: TGetReplyTemplatesResponse) => TData;
}) {
  return useQuery({
    ...getReplyTemplatesQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetReplyTemplatesResponse ? TGetReplyTemplatesResponse : TData>>;
}
